import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { equipmentTypesApi, EquipmentTypeOption } from '../api';
import { queryKeys } from '../queries/keys';

export type EquipmentType = EquipmentTypeOption;

export interface EquipmentTypeVocabulary {
  types: EquipmentType[];
  loading: boolean;
  error: string | null;
  /** True once the taxonomy is known, so a value can be judged against it. */
  loaded: boolean;
  /** The sub-type labels the vocabulary offers under one type key. Empty for a
   *  type with none, and for a key the vocabulary does not hold. */
  subtypesFor: (equipmentTypeKey: string) => string[];
  /** The type's display label; the key itself while the taxonomy loads or for a key it does not hold. */
  labelFor: EquipmentTypeLabel;
}

/** Maps an equipment type key to the label a person reads. */
export type EquipmentTypeLabel = (equipmentTypeKey: string) => string;

export function useEquipmentTypes(): EquipmentTypeVocabulary {
  const query = useQuery({
    queryKey: queryKeys.equipmentTypes(),
    queryFn: () => equipmentTypesApi.list(),
    staleTime: Infinity,
  });

  const types = query.data ?? [];
  const loading = query.isPending;
  const error = query.error
    ? ((query.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      || 'Failed to load equipment types')
    : null;

  const subtypesFor = useCallback(
    (equipmentTypeKey: string): string[] =>
      types.find((item) => item.key === equipmentTypeKey)?.subtypes ?? [],
    [types],
  );

  const labelFor = useCallback<EquipmentTypeLabel>(
    (equipmentTypeKey) => types.find((item) => item.key === equipmentTypeKey)?.label ?? equipmentTypeKey,
    [types],
  );

  return {
    types,
    loading,
    error,
    loaded: !loading && !error && types.length > 0,
    subtypesFor,
    labelFor,
  };
}
